import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../Components/API/Api";
import classes from "./Login.module.css";
import logo from "../../Assest/logo.png";
import hospital from "../../Assest/logo.jpeg";

import {
  FiMail,
  FiLock,
  FiEye,
  FiEyeOff,
  FiShield,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Load saved email if "Remember Me" was checked
  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Validate fields
    if (!email || !password) {
      setError("Please fill in all fields");
      setIsLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      setIsLoading(false);
      return;
    }

    // Password validation
    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      setIsLoading(false);
      return;
    }

    try {
      // API call to login endpoint using axios
      const response = await axios.post(`${BASE_URL}/api/login`, {
        email: email,
        password: password,
      });

      // Check if login was successful
      if (response.data) {
        // Login successful
        const userData = response.data?.data?.user;
        const token = response.data?.data?.token;
        
        // Store user session and token
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("userEmail", email);
        
        // Store the token if returned from API
        if (token) {
          localStorage.setItem("authToken", token);
        }
        
        // Store user data if returned
        if (userData) {
          localStorage.setItem("userData", JSON.stringify(userData));
          if (userData.name) {
            localStorage.setItem("userName", userData.name);
          }
        }
        
        // Remember me functionality
        if (rememberMe) {
          localStorage.setItem("rememberedEmail", email);
        } else {
          localStorage.removeItem("rememberedEmail");
        }
        
        setIsLoading(false);
        // Navigate to dashboard
        navigate("/dashboard");
      } else {
        // Login failed
        setIsLoading(false);
        setError(response.data?.message || "Invalid email or password");
      }
    } catch (error) {
      // Handle different error types
      setIsLoading(false);
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const errorMsg = error.response.data?.message || 
                        error.response.data?.data?.message || 
                        "Invalid email or password";
        setError(errorMsg);
      } else if (error.request) {
        // The request was made but no response was received
        setError("Unable to connect to server. Please check your internet connection.");
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("An unexpected error occurred. Please try again.");
      }
      console.error("Login error:", error);
    }
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  return (
    <div className={classes.container}>
      <div className={classes.left}>
        <img src={hospital} alt="Hospital" className={classes.bgImage} />
        <div className={classes.overlay}></div>

        <div className={classes.leftContent}>
          <div className={classes.logoRow}>
            <img src={logo} alt="logo" className={classes.logo} />
            <div>
              <h2>City Care Hospital</h2>
              <span>Hospital Management System</span>
            </div>
          </div>

          <div className={classes.hero}>
            <h1>
              Better Care.
              <br />
              Smarter Management.
            </h1>
            <div className={classes.line}></div>
            <p>
              Streamline your hospital operations and
              deliver exceptional patient care with our
              comprehensive management system.
            </p>
          </div>

          <div className={classes.featureCard}>
            <div className={classes.feature}>
              <div className={classes.icon}>
                <FiShield />
              </div>
              <div>
                <h4>Secure & Compliant</h4>
                <p>HIPAA compliant with enterprise-level security</p>
              </div>
            </div>

            <div className={classes.feature}>
              <div className={classes.icon}>
                <FiTrendingUp />
              </div>
              <div>
                <h4>Real-time Analytics</h4>
                <p>Make informed decisions with live reporting</p>
              </div>
            </div>

            <div className={classes.feature}>
              <div className={classes.icon}>
                <FiUsers />
              </div>
              <div>
                <h4>Patient Centered</h4>
                <p>Enhance patient experience and quality care</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={classes.right}>
        <div className={classes.loginCard}>
          <h1>Welcome Back</h1>
          <p className={classes.subtitle}>Sign in to your account to continue</p>

          {error && <div className={classes.errorMessage}>{error}</div>}

          <form onSubmit={handleLogin}>
            <div className={classes.formGroup}>
              <label>Email Address</label>
              <div className={classes.input}>
                <FiMail className={classes.inputIcon} />
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            <div className={classes.formGroup}>
              <label>Password</label>
              <div className={classes.input}>
                <FiLock className={classes.inputIcon} />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  required
                />
                <span
                  className={classes.eye}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </span>
              </div>
            </div>

            <div className={classes.row}>
              <label className={classes.remember}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={isLoading}
                />
                Remember me
              </label>
              <button
                type="button"
                className={classes.forgotLink}
                onClick={handleForgotPassword}
                disabled={isLoading}
              >
                Forgot Password?
              </button>
            </div>

            <button
              type="submit"
              className={classes.loginBtn}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className={classes.spinner}>
                  <span className={classes.spinnerDot}>.</span>
                  <span className={classes.spinnerDot}>.</span>
                  <span className={classes.spinnerDot}>.</span>
                </span>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          <div className={classes.divider}>
            <span></span>
            <p>or</p>
            <span></span>
          </div>

          <button className={classes.googleBtn} disabled={isLoading}>
            <img
              src="https://www.svgrepo.com/show/475656/google-color.svg"
              alt="Google"
            />
            Sign in with Google
          </button>

          <div className={classes.contact}>
            Don't have an account?
            <span> Contact Administrator</span>
          </div>
        </div>

        <p className={classes.footer}>
          © 2024 City Care Hospital. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;